import { orgAPI } from "@/api/api";
import { patchFileRecordCaches } from "@/api/fileRecordCache";
import { fileRecordKeys } from "@/api/fileRecords";
import { useCreateFlowSession } from "@/api/flow_sessions";
import { harnessCatalogQuery } from "@/api/harnessCapabilities";
import { usePostFlowMessage } from "@/api/messages";
import { getApiErrorStatus, isApiError } from "@/api/utils";
import { agentView } from "@/components/MessageInput/agentState";
import { config } from "@/config";
import { agentsFromCatalog } from "@/constants/agents";
import { emitCanvasFocusNodes } from "@/pages/canvas/constants/canvasFocusNodes.constants";
import { useCanvasApiOptional } from "@/pages/canvas/context/CanvasContext";
import { useCanvasRoute } from "@/pages/canvas/context/CanvasRouteContext";
import { useCanvasReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import { convertNodesToReferencedEntities } from "@/pages/canvas/utils/convertNodesToReferencedEntities";
import { snapFlowPosition } from "@/pages/canvas/utils/quickAddCursor";
import { normalizeCanvasViewportTarget } from "@/pages/canvas/viewport/canvasViewportTarget";
import { useCanvasViewportNavigator } from "@/pages/canvas/viewport/useCanvasViewportNavigator";
import { Flags } from "@/providers/LaunchDarklyProvider";
import { useIsPublicCanvasView } from "@/public-canvas/PublicCanvasArtifactContext";
import { FLOW_SESSION_MODES, type FileRecord } from "@cohesive/api-client";
import {
  BlockActionFailure,
  isPageFileRecord,
  type BlockActionHandler,
} from "@cohesive/ui-components";
import { useQueryClient } from "@tanstack/react-query";
import { useStoreApi, type Node } from "@xyflow/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * The host side of a block's `cohesive.navigate / chat / generate`: one
 * handler per block node, running every call as the person who clicked.
 *
 * - `navigate`: a node, a set of nodes, a point, or another canvas — the same
 *   moves a block link makes, as a call.
 * - `chat`: a new chat on this canvas with the prompt sent at once and the
 *   pane left closed. The block's own node rides along as a reference, and
 *   the message carries an addendum telling the agent to report into the
 *   block's state at `jobs/<job>` and to place its output beside the block.
 * - `generate`: one structured-output model call, no session, metered to the
 *   viewer like a turn; the block gets the JSON back.
 *
 * The public viewer has no viewer to act as, so only `navigate` works there.
 */
export function useHtmlBlockActions({
  nodeId,
  fileRecordId,
  onNavigate,
  transformHtml,
}: {
  nodeId: string;
  fileRecordId?: string;
  /** The block's link handler: a path into the app, `false` when declined. */
  onNavigate: (to: string) => boolean | void;
  /** The document's own public-link → signed-link transform, so file
   *  descriptors carry the same signed links the frame renews. */
  transformHtml?: (html: string) => Promise<string>;
}): BlockActionHandler {
  const queryClient = useQueryClient();
  const canvasApi = useCanvasApiOptional();
  const { canvasId } = useCanvasRoute();
  const isPublicCanvasView = useIsPublicCanvasView();
  const storeApi = useStoreApi();
  const navigateViewport = useCanvasViewportNavigator();
  const createFlowSession = useCreateFlowSession();
  const postMessage = usePostFlowMessage();
  // The create names no agent, so the server starts the chat on the clicker's own default, on
  // Cohesive where that is a subscription not connected; the message's settings are the agent
  // view of the session the create returns, never a client guess at that default.
  const [reasoningEffort] = useCanvasReasoningEffort(canvasId);
  const flags = useFlags<Flags>();
  // A double click is one chat, not two: the same input within the window
  // gets the first call's answer.
  const recentChatRef = useRef<{
    key: string;
    at: number;
    result: Promise<unknown>;
  } | null>(null);
  // ponytail: a per-block window in this tab is the ceiling; the API meters
  // the viewer's credits either way.
  const generateCallsRef = useRef<number[]>([]);

  const navigate = useCallback(
    async (input: unknown): Promise<unknown> => {
      const to = asRecord(input);
      const otherCanvas =
        typeof to.canvas === "string" && to.canvas !== canvasId
          ? to.canvas
          : null;
      if (otherCanvas !== null) {
        if (!UUID_RE.test(otherCanvas)) {
          throw new BlockActionFailure(
            "invalid",
            "canvas must be a canvas id.",
          );
        }
        // Leaving the canvas tears this block down, so a wrong id must fail
        // here, not as a 404 page the person lands on.
        try {
          await orgAPI.canvas.getById(otherCanvas);
        } catch (err) {
          throw apiFailure(err, {
            404: ["not_found", "No such canvas."],
            403: ["unavailable", "You cannot open that canvas."],
          });
        }
        const hash = nodeHash(to) ?? viewportHash(to);
        if (onNavigate(`/canvas/${otherCanvas}${hash}`) === false) {
          throw new BlockActionFailure(
            "unavailable",
            "Other canvases cannot be opened from here.",
          );
        }
        return { ok: true };
      }
      const ids = nodeIdsOf(to);
      if (ids) {
        if (!emitCanvasFocusNodes(ids)) {
          throw new BlockActionFailure(
            "not_found",
            "No such node on this canvas.",
          );
        }
        return { ok: true };
      }
      const target = normalizeCanvasViewportTarget(to);
      if (!target) {
        throw new BlockActionFailure(
          "invalid",
          "navigate takes { node }, { nodes }, { x, y } or { canvas }.",
        );
      }
      if (!navigateViewport(target)) {
        throw new BlockActionFailure(
          "failed",
          "The canvas is not ready to move.",
        );
      }
      return { ok: true };
    },
    [canvasId, navigateViewport, onNavigate],
  );

  const chat = useCallback(
    (input: unknown): Promise<unknown> => {
      if (isPublicCanvasView || !canvasId) {
        throw new BlockActionFailure(
          "unavailable",
          "Chats cannot be started from a published canvas.",
        );
      }
      const spec = asRecord(input);
      const prompt = typeof spec.prompt === "string" ? spec.prompt.trim() : "";
      if (!prompt || prompt.length > CHAT_PROMPT_MAX_CHARS) {
        throw new BlockActionFailure(
          "invalid",
          `chat takes a prompt of 1 to ${CHAT_PROMPT_MAX_CHARS} characters.`,
        );
      }
      const refs = stringList(spec.references);
      if (!refs || refs.length > CHAT_REFERENCES_MAX) {
        throw new BlockActionFailure(
          "invalid",
          `references must be a list of at most ${CHAT_REFERENCES_MAX} node ids.`,
        );
      }
      const nodes = storeApi.getState().nodes;
      const byId = new Map(nodes.map((node) => [node.id, node]));
      const refNodes = refs.map((id) => byId.get(id));
      if (refNodes.some((node) => node === undefined)) {
        throw new BlockActionFailure(
          "not_found",
          "A referenced node is not on this canvas.",
        );
      }
      const self = byId.get(nodeId);
      const key = JSON.stringify([fileRecordId, prompt, refs]);
      const recent = recentChatRef.current;
      if (
        recent &&
        recent.key === key &&
        Date.now() - recent.at < CHAT_DEDUPE_MS
      ) {
        return recent.result;
      }
      const job = uuidv4();
      const result = (async () => {
        // No harness or model: the server fills the clicker's default and
        // skips one that no longer applies, which this tab's canvas draft
        // would not.
        const session = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_CHAT,
          class: "canvas",
          canvas_id: canvasId,
        });
        // Fetched here, not subscribed: the public viewer mounts this hook and has no catalog.
        const catalog = await queryClient.ensureQueryData(harnessCatalogQuery);
        const view = agentView({
          flowSessionId: session.id,
          session,
          hasDraftOwner: false,
          agents: agentsFromCatalog(catalog),
          capabilities: catalog.harness_capabilities,
          harnessRuntime: !!flags.harnessRuntime,
          hostTier: reasoningEffort,
        });
        await postMessage.mutateAsync({
          flowSessionId: session.id,
          id: uuidv4(),
          content: prompt,
          referenced_entities: convertNodesToReferencedEntities(
            dedupeNodes([self, ...refNodes]),
          ),
          addendum: blockChatAddendum({ job, nodeId, fileRecordId, self }),
          ...view.sendSettings,
        });
        return { session_id: session.id, job };
      })().catch((err: unknown) => {
        throw apiFailure(err);
      });
      recentChatRef.current = { key, at: Date.now(), result };
      return result;
    },
    [
      canvasId,
      createFlowSession,
      fileRecordId,
      flags.harnessRuntime,
      queryClient,
      reasoningEffort,
      isPublicCanvasView,
      nodeId,
      postMessage,
      storeApi,
    ],
  );

  /** A file record in the block's vocabulary: its public link (what the
   *  block writes into the DOM) and the signed link that serves it now. */
  const describeFiles = useCallback(
    async (
      records: (FileRecord | null)[],
      extra: Map<string, { width?: number; height?: number; node_id?: string }>,
    ) => {
      const present = records.filter((r): r is FileRecord => r !== null);
      const publicUrls = present.map((r) => publicFileUrl(r.id));
      const signed = new Map<string, string>();
      if (transformHtml && publicUrls.length > 0) {
        try {
          const out = (await transformHtml(publicUrls.join("\n"))).split("\n");
          publicUrls.forEach((u, i) => {
            if (out[i] && out[i] !== u) signed.set(u, out[i]);
          });
        } catch {
          /* the record's own link stands in */
        }
      }
      return records.map((r) => {
        if (!r) return null;
        const publicUrl = publicFileUrl(r.id);
        const x = extra.get(r.id) ?? {};
        return {
          id: r.id,
          name: r.path.split("/").pop() || r.id,
          kind: r.content_type,
          width: x.width ?? r.width ?? undefined,
          height: x.height ?? r.height ?? undefined,
          url: signed.get(publicUrl) ?? r.url ?? publicUrl,
          publicUrl,
          node_id: x.node_id,
        };
      });
    },
    [transformHtml],
  );

  /** Puts a file card on the canvas: at `place` when it is a point, else in
   *  free space to the right of the block. Returns the node id. */
  const placeFile = useCallback(
    async (
      record: FileRecord,
      dims: { width?: number; height?: number },
      place: unknown,
    ): Promise<string | undefined> => {
      if (!canvasApi) return undefined;
      const { entity, nodeWidth, nodeHeight } = fileCardFor(record, dims);
      const nodes = storeApi.getState().nodes;
      const p = asRecord(place);
      const position =
        typeof p.x === "number" && typeof p.y === "number"
          ? snapFlowPosition({ x: p.x, y: p.y })
          : freeSlotBeside(nodes, nodeId, nodeWidth, nodeHeight);
      canvasApi.addNode({
        id: record.id,
        type: "entity",
        position,
        width: nodeWidth,
        height: nodeHeight,
        data: { entity },
      } as Node);
      return record.id;
    },
    [canvasApi, nodeId, storeApi],
  );

  const uploadIntoCanvas = useCallback(
    async (file: File) => {
      if (!canvasId) {
        throw new BlockActionFailure(
          "unavailable",
          "This block is not on a canvas.",
        );
      }
      const dims = await imageDimensionsOf(file);
      // The record, then the bytes. The first free numbered name is picked
      // from the canvas's files, as the dashboard's uploader does, and a
      // 409 (someone took it meanwhile) moves on to the next free one.
      const taken = new Set(
        (
          await orgAPI.fileRecords
            .list({ class: "canvas", canvas_id: canvasId })
            .catch(() => [] as FileRecord[])
        ).map((r) => r.path.split("/").pop() ?? ""),
      );
      let n = 0;
      let name = file.name;
      const nextFree = () => {
        while (taken.has(name)) name = numberedName(file.name, ++n);
      };
      nextFree();
      let record: FileRecord | undefined;
      for (let attempt = 0; attempt < 4 && !record; attempt++) {
        try {
          record = await orgAPI.fileRecords.create({
            provider: "s3",
            size: file.size,
            destination: { class: "canvas", canvas_id: canvasId, path: name },
          });
        } catch (err) {
          if (getApiErrorStatus(err) === 409 && attempt < 3) {
            taken.add(name);
            nextFree();
            continue;
          }
          throw apiFailure(err);
        }
      }
      if (!record)
        throw new BlockActionFailure(
          "failed",
          "The file could not be created.",
        );
      patchFileRecordCaches(queryClient, record);
      if (record.upload_url) {
        const form = new FormData();
        for (const [k, v] of Object.entries(record.upload_field_values ?? {})) {
          form.append(k, v);
        }
        form.append("Content-Type", file.type);
        form.append("file", file);
        const res = await fetch(record.upload_url, {
          method: "POST",
          body: form,
        });
        if (!res.ok) {
          throw new BlockActionFailure(
            "failed",
            `The upload failed (${res.status}).`,
          );
        }
      }
      // The record is served once the bucket has told the API about the
      // bytes, a moment after the POST: wait for its link, so the descriptor
      // and the public link both work the instant the block gets them.
      const id = record.id;
      const started = Date.now();
      while (!record.url && Date.now() - started < UPLOAD_SERVED_WAIT_MS) {
        await new Promise((r) => setTimeout(r, UPLOAD_SERVED_POLL_MS));
        try {
          record = await queryClient.fetchQuery<FileRecord>({
            queryKey: fileRecordKeys.detail(id),
            queryFn: () => orgAPI.fileRecords.getById(id),
            staleTime: 0,
            retry: false,
          });
        } catch {
          /* not there yet */
        }
      }
      return { record, width: dims?.width, height: dims?.height };
    },
    [canvasId, queryClient],
  );

  const filesUpload = useCallback(
    async (input: unknown): Promise<unknown> => {
      if (isPublicCanvasView) {
        throw new BlockActionFailure(
          "unavailable",
          "Files cannot be uploaded from a published canvas.",
        );
      }
      const spec = asRecord(input);
      const blob = spec.file;
      if (!(blob instanceof Blob)) {
        throw new BlockActionFailure("invalid", "files.upload takes a File.");
      }
      if (blob.size === 0 || blob.size > UPLOAD_MAX_BYTES) {
        throw new BlockActionFailure(
          "invalid",
          `files.upload takes a file of 1 byte to ${UPLOAD_MAX_BYTES / 1048576} MB.`,
        );
      }
      const type = blob.type;
      if (!type || !UPLOAD_EXT_BY_TYPE.has(type)) {
        throw new BlockActionFailure(
          "invalid",
          "That file type cannot be uploaded here.",
        );
      }
      const given =
        typeof spec.name === "string" && spec.name.trim()
          ? spec.name
          : blob instanceof File
            ? blob.name
            : "";
      const file = new File([blob], safeFileName(given || "upload", type), {
        type,
      });
      const uploaded = await uploadIntoCanvas(file);
      const dims = { width: uploaded.width, height: uploaded.height };
      const node_id =
        spec.place === undefined || spec.place === false
          ? undefined
          : await placeFile(uploaded.record, dims, spec.place);
      const [described] = await describeFiles(
        [uploaded.record],
        new Map([[uploaded.record.id, { ...dims, node_id }]]),
      );
      return described;
    },
    [describeFiles, isPublicCanvasView, placeFile, uploadIntoCanvas],
  );

  const filesGet = useCallback(
    async (input: unknown): Promise<unknown> => {
      if (isPublicCanvasView) {
        throw new BlockActionFailure(
          "unavailable",
          "files.get is not available on a published canvas.",
        );
      }
      const ids = stringList(asRecord(input).ids);
      if (
        !ids ||
        ids.length === 0 ||
        ids.length > FILES_GET_MAX ||
        !ids.every((id) => UUID_RE.test(id))
      ) {
        throw new BlockActionFailure(
          "invalid",
          `files.get takes 1 to ${FILES_GET_MAX} file ids.`,
        );
      }
      const records = await Promise.all(
        ids.map(async (id) => {
          try {
            return await queryClient.fetchQuery<FileRecord>({
              queryKey: fileRecordKeys.detail(id),
              queryFn: () => orgAPI.fileRecords.getById(id),
              staleTime: 60_000,
              retry: false,
            });
          } catch (err) {
            const status = getApiErrorStatus(err);
            if (status === 400 || status === 403 || status === 404) return null;
            throw apiFailure(err);
          }
        }),
      );
      return describeFiles(records, new Map());
    },
    [describeFiles, isPublicCanvasView, queryClient],
  );

  const generateImage = useCallback(
    async (
      prompt: string,
      spec: Record<string, unknown>,
      { sources, aspect }: ImageSpec,
      refund: () => void,
    ): Promise<unknown> => {
      let text: string;
      try {
        const organizationId = await orgAPI.getOrgID();
        const userId = await orgAPI.getUserID();
        const res = await orgAPI.axios.post<string>(
          `/api/v2/org/${organizationId}/user/${userId}/generate/image`,
          {
            prompt,
            source_file_record_ids: sources,
            aspect_ratio: aspect,
          },
          {
            responseType: "text",
            headers: { accept: "text/event-stream" },
            timeout: IMAGE_TIMEOUT_MS,
          },
        );
        text =
          typeof res.data === "string" ? res.data : JSON.stringify(res.data);
      } catch (err) {
        // A request the server refused as malformed never reached a model.
        if (getApiErrorStatus(err) === 400) refund();
        throw apiFailure(err, {
          400: [
            "invalid",
            serverMessage(err) ?? "The image request was refused.",
          ],
        });
      }
      const event = lastSseEvent(text);
      if (!event) {
        throw new BlockActionFailure(
          "failed",
          "The image service sent no result.",
        );
      }
      if (event.name === "error") {
        const { status, message } = event.data as {
          status?: number;
          message?: string;
        };
        throw new BlockActionFailure(
          status === 402
            ? "credits"
            : status === 429
              ? "rate_limited"
              : status === 400
                ? "invalid"
                : "failed",
          message || "The image could not be made.",
        );
      }
      const { content_type, data_base64 } = event.data as {
        content_type?: string;
        data_base64?: string;
      };
      if (!content_type || !data_base64) {
        throw new BlockActionFailure(
          "failed",
          "The image service sent no image.",
        );
      }
      const bytes = Uint8Array.from(atob(data_base64), (c) => c.charCodeAt(0));
      const given =
        typeof spec.name === "string" && spec.name.trim()
          ? spec.name
          : slugOf(prompt);
      const file = new File([bytes], safeFileName(given, content_type), {
        type: content_type,
      });
      const uploaded = await uploadIntoCanvas(file);
      const dims = { width: uploaded.width, height: uploaded.height };
      const node_id =
        spec.place === false
          ? undefined
          : await placeFile(uploaded.record, dims, spec.place);
      const [described] = await describeFiles(
        [uploaded.record],
        new Map([[uploaded.record.id, { ...dims, node_id }]]),
      );
      return described;
    },
    [describeFiles, placeFile, uploadIntoCanvas],
  );

  const generate = useCallback(
    async (input: unknown): Promise<unknown> => {
      if (isPublicCanvasView) {
        throw new BlockActionFailure(
          "unavailable",
          "generate is not available on a published canvas.",
        );
      }
      const spec = asRecord(input);
      const prompt = typeof spec.prompt === "string" ? spec.prompt.trim() : "";
      if (!prompt || prompt.length > CHAT_PROMPT_MAX_CHARS) {
        throw new BlockActionFailure(
          "invalid",
          `generate takes a prompt of 1 to ${CHAT_PROMPT_MAX_CHARS} characters.`,
        );
      }
      const image = spec.output === "image";
      const imageSpec = image ? validateImageSpec(spec) : null;
      const schema = asRecord(spec.schema);
      if (!image && schema.type !== "object") {
        throw new BlockActionFailure(
          "invalid",
          'generate takes a JSON schema with type "object".',
        );
      }
      const inputText =
        spec.input === undefined
          ? prompt
          : `${prompt}\n\nInput (JSON):\n${JSON.stringify(spec.input)}`;
      if (inputText.length > GENERATE_INPUT_MAX_CHARS) {
        throw new BlockActionFailure(
          "invalid",
          `generate takes at most ${GENERATE_INPUT_MAX_CHARS} characters of prompt and input together.`,
        );
      }
      const now = Date.now();
      const calls = generateCallsRef.current.filter(
        (at) => now - at < GENERATE_WINDOW_MS,
      );
      if (calls.length >= GENERATE_WINDOW_CALLS) {
        throw new BlockActionFailure(
          "rate_limited",
          `At most ${GENERATE_WINDOW_CALLS} generate calls a minute per block.`,
          calls[0] + GENERATE_WINDOW_MS - now,
        );
      }
      calls.push(now);
      generateCallsRef.current = calls;
      const refund = () => {
        generateCallsRef.current = generateCallsRef.current.filter(
          (at) => at !== now,
        );
      };
      if (imageSpec) return generateImage(prompt, spec, imageSpec, refund);
      let response;
      try {
        response = await orgAPI.genai.generate({
          input_text: inputText,
          output_schema: schema as never,
        });
      } catch (err) {
        // A request the server refused as malformed never reached a model,
        // so it does not spend the block's minute.
        if (getApiErrorStatus(err) === 400) {
          generateCallsRef.current = calls.filter((at) => at !== now);
        }
        throw apiFailure(err, {
          400: [
            "invalid",
            serverMessage(err) ?? "The schema or input was refused.",
          ],
        });
      }
      if (response.moderation_flagged) {
        throw new BlockActionFailure(
          "failed",
          "The request was flagged by moderation.",
        );
      }
      return response.structured_output ?? null;
    },
    [isPublicCanvasView, generateImage],
  );

  return useCallback<BlockActionHandler>(
    (name, input) => {
      switch (name) {
        case "files.upload":
          return filesUpload(input);
        case "files.get":
          return filesGet(input);
        case "navigate":
          return navigate(input);
        case "chat":
          return chat(input);
        case "generate":
          return generate(input);
        default:
          return Promise.reject(
            new BlockActionFailure("invalid", `Unknown action "${name}".`),
          );
      }
    },
    [navigate, chat, generate, filesUpload, filesGet],
  );
}

interface ImageSpec {
  sources: string[];
  aspect?: string;
}

/** The image call's own inputs, checked before the block's window is spent. */
function validateImageSpec(spec: Record<string, unknown>): ImageSpec {
  const sources = stringList(spec.sources);
  if (
    !sources ||
    sources.length > IMAGE_SOURCES_MAX ||
    !sources.every((id) => UUID_RE.test(id))
  ) {
    throw new BlockActionFailure(
      "invalid",
      `sources must be up to ${IMAGE_SOURCES_MAX} file ids.`,
    );
  }
  const aspect = spec.aspect === undefined ? undefined : String(spec.aspect);
  if (aspect !== undefined && !IMAGE_ASPECTS.has(aspect)) {
    throw new BlockActionFailure(
      "invalid",
      `aspect must be one of ${Array.from(IMAGE_ASPECTS).join(", ")}.`,
    );
  }
  return { sources, aspect };
}

const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;
const UPLOAD_SERVED_WAIT_MS = 20_000;
const UPLOAD_SERVED_POLL_MS = 500;
const FILES_GET_MAX = 32;
const IMAGE_SOURCES_MAX = 4;
const IMAGE_TIMEOUT_MS = 10 * 60 * 1000;
const IMAGE_ASPECTS = new Set([
  "1:1",
  "2:3",
  "3:2",
  "3:4",
  "4:3",
  "4:5",
  "5:4",
  "9:16",
  "16:9",
  "21:9",
]);

/** The public form of a file link, which every surface rewrites by id. The
 *  env's own files host when it has the canonical shape, else the canonical
 *  host: the id is what matters. */
function publicFileUrl(id: string): string {
  const host = /^https:\/\/file\.(?:[a-z0-9-]+\.)?cohesive\.ai$/.test(
    config.hosts.file,
  )
    ? config.hosts.file
    : "https://file.cohesive.ai";
  return `${host}/public/${id}`;
}

/** A file name the platform accepts: no paths, bounded, with the extension
 *  its type implies. */
/** What a block may upload, and the extension each type gets. HEIC is out:
 *  no browser shows it, and the converter is too heavy for every surface. */
const UPLOAD_EXT_BY_TYPE = new Map<string, string>([
  ["image/png", "png"],
  ["image/jpeg", "jpg"],
  ["image/webp", "webp"],
  ["image/gif", "gif"],
  ["image/bmp", "bmp"],
  ["image/tiff", "tiff"],
  ["image/svg+xml", "svg"],
  ["application/pdf", "pdf"],
  ["text/plain", "txt"],
  ["text/markdown", "md"],
  ["text/csv", "csv"],
  ["text/html", "html"],
  ["application/json", "json"],
  ["application/msword", "doc"],
  [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "docx",
  ],
  ["application/vnd.ms-excel", "xls"],
  ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "xlsx"],
  ["application/vnd.ms-powerpoint", "ppt"],
  [
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "pptx",
  ],
]);

function numberedName(name: string, n: number): string {
  const dot = name.lastIndexOf(".");
  return dot > 0
    ? `${name.slice(0, dot)} (${n})${name.slice(dot)}`
    : `${name} (${n})`;
}

/** Pixel size of an image file the browser can decode; undefined otherwise. */
async function imageDimensionsOf(
  file: File,
): Promise<{ width: number; height: number } | undefined> {
  if (
    !file.type.startsWith("image/") ||
    typeof createImageBitmap !== "function"
  ) {
    return undefined;
  }
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return undefined;
  }
}

/** The card a file gets on the canvas: images at their aspect with the long
 *  side at 320, pages (documents, PDFs, office files) portrait, anything
 *  else the landscape file tile. */
function fileCardFor(
  record: FileRecord,
  dims: { width?: number; height?: number },
): {
  entity: Record<string, unknown>;
  nodeWidth: number;
  nodeHeight: number;
} {
  const ct = record.content_type || "";
  const w = dims.width ?? record.width;
  const h = dims.height ?? record.height;
  const fitted = ct.startsWith("image/") && !!w && !!h && w > 0 && h > 0;
  const page = isPageFileRecord({
    content_type: ct,
    origin_type: record.origin_type,
  });
  let nodeWidth = 320;
  let nodeHeight = page ? 416 : 192;
  if (fitted) {
    const long = Math.max(w, h);
    nodeWidth = (w / long) * 320;
    nodeHeight = (h / long) * 320;
  }
  return {
    entity: {
      type: "file_record",
      base: "file_record",
      platform: "cohesive",
      data: {
        id: record.id,
        title: record.path.split("/").pop() || record.id,
        ...(ct ? { content_type: ct } : {}),
        ...(fitted ? { width: w, height: h } : {}),
      },
    },
    nodeWidth,
    nodeHeight,
  };
}

function safeFileName(name: string, type: string): string {
  const ext = UPLOAD_EXT_BY_TYPE.get(type);
  let base = name
    .split(/[\\/]/)
    .pop()!
    .replace(/[\x00-\x1f<>:"|?*]/g, "")
    .trim()
    .slice(0, 120);
  if (!base) base = "file";
  const hasExt = /\.[a-z0-9]{1,5}$/i.test(base);
  if (ext && !hasExt) return `${base}.${ext}`;
  if (ext && hasExt) {
    const current = base.slice(base.lastIndexOf(".") + 1).toLowerCase();
    const sameType =
      UPLOAD_EXT_BY_TYPE.get(type) === current ||
      (type === "image/jpeg" && current === "jpeg");
    return sameType ? base : `${base.slice(0, base.lastIndexOf("."))}.${ext}`;
  }
  return base;
}

function slugOf(prompt: string): string {
  const words = prompt.toLowerCase().match(/[a-z0-9]+/g) ?? [];
  const slug = words.slice(0, 6).join("-").slice(0, 48);
  return `${slug || "image"}-${Date.now().toString(36)}`;
}

/** The last `result` or `error` event of a server-sent event stream. */
function lastSseEvent(text: string): { name: string; data: unknown } | null {
  let last: { name: string; data: unknown } | null = null;
  for (const block of text.split(/\r?\n\r?\n/)) {
    let name = "message";
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trim());
    }
    if (name !== "result" && name !== "error") continue;
    try {
      last = { name, data: JSON.parse(data.join("\n")) };
    } catch {
      /* a partial block */
    }
  }
  return last;
}

/** Free space to the right of the block, scanning down the column and then
 *  the next one over, on the 16 px lattice. */
function freeSlotBeside(
  nodes: Node[],
  selfId: string,
  width: number,
  height: number,
): { x: number; y: number } {
  const boxOf = (n: Node) => ({
    x: n.position.x,
    y: n.position.y,
    w: n.measured?.width ?? n.width ?? 0,
    h: n.measured?.height ?? n.height ?? 0,
  });
  const self = nodes.find((n) => n.id === selfId);
  const origin = self ? boxOf(self) : { x: 0, y: 0, w: 0, h: 0 };
  const boxes = nodes.filter((n) => n.id !== selfId).map(boxOf);
  const clear = (x: number, y: number) =>
    !boxes.some(
      (b) =>
        x < b.x + b.w + 16 &&
        x + width + 16 > b.x &&
        y < b.y + b.h + 16 &&
        y + height + 16 > b.y,
    );
  let x = origin.x + origin.w + 32;
  for (let column = 0; column < 8; column++) {
    let y = origin.y;
    for (let row = 0; row < 200; row++) {
      const pos = snapFlowPosition({ x, y });
      if (clear(pos.x, pos.y)) return pos;
      y += 16;
    }
    x += width + 32;
  }
  return snapFlowPosition({ x: origin.x + origin.w + 32, y: origin.y });
}

const CHAT_PROMPT_MAX_CHARS = 4000;
const CHAT_REFERENCES_MAX = 32;
const CHAT_DEDUPE_MS = 3000;
const GENERATE_INPUT_MAX_CHARS = 64_000;
const GENERATE_WINDOW_MS = 60_000;
const GENERATE_WINDOW_CALLS = 10;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : {};
}

function stringList(value: unknown): string[] | null {
  if (value === undefined) return [];
  if (!Array.isArray(value)) return null;
  return value.every((v) => typeof v === "string") ? (value as string[]) : null;
}

function nodeIdsOf(to: Record<string, unknown>): string[] | null {
  if (typeof to.node === "string" && to.node) return [to.node];
  const nodes = stringList(to.nodes);
  return nodes && nodes.length > 0 ? nodes : null;
}

function nodeHash(to: Record<string, unknown>): string | null {
  const ids = nodeIdsOf(to);
  return ids ? `#nodes=${ids.map(encodeURIComponent).join(",")}` : null;
}

function viewportHash(to: Record<string, unknown>): string {
  const target = normalizeCanvasViewportTarget(to);
  if (!target) return "";
  const params = new URLSearchParams({
    x: String(target.x),
    y: String(target.y),
  });
  if (target.zoom !== undefined) params.set("z", String(target.zoom));
  return `#${params.toString()}`;
}

function dedupeNodes(nodes: (Node | undefined)[]): Node[] {
  const seen = new Set<string>();
  const out: Node[] = [];
  for (const node of nodes) {
    if (!node || seen.has(node.id)) continue;
    seen.add(node.id);
    out.push(node);
  }
  return out;
}

// The dashboard's response interceptor turns every failed call into an
// ApiError carrying the zensei envelope.
function serverMessage(err: unknown): string | undefined {
  if (!isApiError(err)) return undefined;
  const body = err.data as
    | { error?: string | { message?: unknown } | null }
    | undefined;
  const error = body?.error;
  const message = typeof error === "string" ? error : error?.message;
  return typeof message === "string" && message ? message : undefined;
}

/** The API's refusal, in the block's vocabulary; `byStatus` adds the
 *  call's own meanings for specific statuses. */
function apiFailure(
  err: unknown,
  byStatus: Record<number, [BlockActionFailure["code"], string]> = {},
): BlockActionFailure {
  if (err instanceof BlockActionFailure) return err;
  const status = getApiErrorStatus(err);
  if (status !== undefined && byStatus[status]) {
    const [code, message] = byStatus[status];
    return new BlockActionFailure(code, message);
  }
  if (status === 402) {
    return new BlockActionFailure("credits", "No credits left for this.");
  }
  if (status === 429) {
    return new BlockActionFailure(
      "rate_limited",
      "Too many requests; try again shortly.",
    );
  }
  if (status === 403) {
    return new BlockActionFailure(
      "unavailable",
      "You cannot do this on this canvas.",
    );
  }
  if (status === 501) {
    return new BlockActionFailure(
      "unavailable",
      "This is not set up in this environment.",
    );
  }
  return new BlockActionFailure(
    "failed",
    err instanceof Error && err.message ? err.message : "The request failed.",
  );
}

/**
 * What the agent reads after the prompt and the person never sees: where the
 * message came from and how to report back to the block that sent it.
 */
export function blockChatAddendum({
  job,
  nodeId,
  fileRecordId,
  self,
}: {
  job: string;
  nodeId: string;
  fileRecordId?: string;
  self?: Node;
}): string {
  const where = self
    ? ` It sits at x=${Math.round(self.position.x)}, y=${Math.round(self.position.y)}` +
      (self.measured?.width && self.measured?.height
        ? `, ${Math.round(self.measured.width)}×${Math.round(self.measured.height)}.`
        : ".")
    : "";
  return [
    `This message was sent by a button in the HTML block on this canvas (node ${nodeId}` +
      (fileRecordId ? `, file ${fileRecordId}` : "") +
      `).${where}`,
    "The person is watching that block, not this chat, so report through the block's state " +
      `with your block state tool, never through a shell. The call is FileRecords_StateUpdate with ` +
      `{ "file_record_id": "${fileRecordId ?? "<the file above>"}", "ops": [{ "op": "set", "key": "jobs/${job}", "value": <status> }] } ` +
      `(on an MCP connection the tool is file_state_update). Write <status> as ` +
      `{ "status": "running", "message": "<what you are doing>" } when you start, ` +
      `{ "status": "done", "message": "<one line>", "result": { "node_id": "<the node you made>" } } when finished, ` +
      `or { "status": "failed", "message": "<why>" } if you cannot. One call each; do not retry a call that returned no error.`,
    "If you only have the CLI, the same write is `cohesive file state update <file id> --ops '<the ops array>'`.",
    "Place anything you create on this canvas in free space next to that block: read the canvas first and do not overlap existing nodes. Keep the reply short: the block shows your status messages.",
  ].join("\n");
}
